// packages/chatbot/src/hooks/useChatbotController.ts

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useConversation, useAgents } from '@dopomogai/agent-kit';
import type { InitTestChatResponse, SSEMessageEventData } from '@dopomogai/types';
import type { ChatbotProps, SendMessageOptions, ConversationSummary } from '../types';
import { NEW_CHAT_ID } from '../utils/constants';
import { getIsSidebarVisible, setIsSidebarVisible as setSidebarVisibleFlag } from '../utils/storage';
import { useConversationPersistence } from './controller/useConversationPersistence';
import { useTransientNewChatMemory } from './controller/useTransientNewChatMemory';
import { useHistoryStrategy } from './controller/useHistoryStrategy';
import { useConversationSelection } from './controller/useConversationSelection';
import { useHeadlessToolRuntime } from './controller/useHeadlessToolRuntime';
import { useAssistantState } from './controller/useAssistantState';
import { getWidFromWat } from '../utils/widget';
import { computeConfigHash } from '../utils/configVersion';

function sortByUpdatedDesc(items: ConversationSummary[]): ConversationSummary[] {
  const copy = [...(items || [])];
  copy.sort((a, b) => {
    const ta = new Date(a.updated_at || a.created_at || 0).getTime();
    const tb = new Date(b.updated_at || b.created_at || 0).getTime();
    return tb - ta;
  });
  return copy;
}

export function useChatbotController(params: ChatbotProps) {
  const {
    agentId,
    authMode = 'test',
    widgetInit,
    perTurnContext,
    perTurnPrompt,
    initialContext,
    onCommand,
    mode = 'full',
    memoryScope = 'site',
    headlessTools,
    headlessContext,
    initialMessage,
  } = params;

  const isWidget = authMode === 'widget';
  const DBG = useMemo(() => {
    try {
      const sp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
      return sp.has('botDebug') || (window as any).__DG_DEBUG;
    } catch { return false; }
  }, []);

  const adoptionLatchRef = useRef<{ id: string; ts: number } | null>(null);
  const userInteractedRef = useRef(false);
  const loadedForRef = useRef<string | null>(null);
  const [isHydratingHistory, setIsHydratingHistory] = useState<boolean>(false);

  const [activeAgentId, setActiveAgentId] = useState<string | null>(agentId ?? null);
  const [isSidebarVisible, setIsSidebarVisible] = useState<boolean>(() => getIsSidebarVisible());
  useEffect(() => { setSidebarVisibleFlag(isSidebarVisible); }, [isSidebarVisible]);

  const [sessionConfig, setSessionConfig] = useState<InitTestChatResponse | null>(null);
  const [sseLogs, setSseLogs] = useState<{ timestamp: number; data: SSEMessageEventData }[]>([]);
  const [lastSentOverlays, setLastSentOverlays] = useState<object | null>(null);
  const [lastReceivedHeaders, setLastReceivedHeaders] = useState<object | null>(null);

  const log = useCallback((...args: any[]) => { 
    if (DBG) console.debug('[ChatController]', ...args); 
  }, [DBG]);

  // MEMOIZED: Prevent infinite loop in useConversation -> useChatSession
  const handleSseEvent = useCallback((eventData: SSEMessageEventData) => {
    setSseLogs(prev => {
      // Cap log size to prevent memory leaks
      const next = [...prev, { timestamp: Date.now(), data: eventData }];
      return next.length > 50 ? next.slice(-50) : next;
    });
    
    if (eventData.type === 'init') {
      const headers = (eventData as any)._headers;
      if (headers) setLastReceivedHeaders(headers);
    }
    if (DBG) console.debug('[SSE]', eventData.type, eventData);
  }, [DBG]);

  const handleSessionInit = useCallback((config: InitTestChatResponse) => {
    setSessionConfig(config);
    if (config.agent_id && isWidget) setActiveAgentId(config.agent_id);
    log('sessionInit', config);
  }, [isWidget, log]);

  // MEMOIZED: Stable widget init object to stop session regeneration loops
  const stableWidgetInit = useMemo(() => {
      if (!widgetInit) return undefined;
      return {
        widgetId: widgetInit.widgetId,
        templateId: widgetInit.templateId,
        apiBaseUrlOverride: widgetInit.apiBaseUrlOverride
      };
  }, [widgetInit?.widgetId, widgetInit?.templateId, widgetInit?.apiBaseUrlOverride]);

  const [initialTargetId, setInitialTargetId] = useState<string | null>(NEW_CHAT_ID);
  const wid = sessionConfig?.wat ? getWidFromWat(sessionConfig.wat) : null;
  const conversationPersistence = useConversationPersistence({
    scope: memoryScope, agentId: activeAgentId, resumeKeySuffix: isWidget ? (wid || undefined) : undefined,
  });

  const stubHistoryStrategy = useMemo(() => ({
    list: { items: [], isLoading: false, error: null, isMoreLoading: false, hasMore: false, loadMore: () => {}, refresh: () => {} },
    loadFullTranscript: async () => null,
  }), []);

  const didAutoSelectRef = useRef(false);
  const {
    activeConversationId: selectionActiveId,
    onSelectConversation,
    adoptActiveConversationId,
    isNewChatPending,
  } = useConversationSelection({
    isWidget,
    mode,
    scope: memoryScope,
    agentId: activeAgentId,
    sessionReady: Boolean(sessionConfig?.wat && sessionConfig?.api_base_url),
    historyStrategy: stubHistoryStrategy,
    conversationPersistence,
    initialActiveId: initialTargetId,
    onBeforeSelect: () => {
      userInteractedRef.current = true;
      didAutoSelectRef.current = true;
      log('onBeforeSelect');
    },
  });

  const {
    messages,
    setMessages,
    sendMessage,
    submitToolResult,
    isLoading,
    error: messageError,
    conversationId,
    loadHistory,
  } = useConversation(
    activeAgentId,
    selectionActiveId === NEW_CHAT_ID ? null : selectionActiveId,
    {
      authMode,
      widgetInit: stableWidgetInit,
      onSessionInit: handleSessionInit,
      onSseEvent: handleSseEvent,
    }
  );

  const historyStrategy = useHistoryStrategy({
    isWidget,
    activeAgentId,
    apiBaseUrl: sessionConfig?.api_base_url,
    wat: sessionConfig?.wat,
    loadHistory,
  });

  const { agents, error: agentsError } = isWidget ? { agents: [], error: null as any } : useAgents();
  useEffect(() => {
    if (isWidget) return;
    if (activeAgentId) return;
    if (Array.isArray(agents) && agents.length > 0) {
      setActiveAgentId(agents[0].id);
      log('defaultAgent', agents[0].id);
    }
  }, [isWidget, activeAgentId, agents, log]);  

  useEffect(() => { loadedForRef.current = null; }, [selectionActiveId]);

  // --- WIDGET Hydration (Fixed Race Condition & Wipe) ---
  useEffect(() => {
    if (!isWidget) return;
    if (!selectionActiveId || selectionActiveId === NEW_CHAT_ID) return;

    // Anti-Wipe Guard: If we have local messages, assume they are fresher/optimistic.
    // Don't overwrite with potentially stale DB state unless explicit.
    if (messages.length > 0) {
        if (loadedForRef.current !== selectionActiveId) {
            loadedForRef.current = selectionActiveId;
        }
        return;
    }

    const latch = adoptionLatchRef.current;
    const inAdoptionWindow = latch && latch.id === selectionActiveId && Date.now() - latch.ts < 2500;
    if (inAdoptionWindow) return;

    if (loadedForRef.current === selectionActiveId) return;

    // Strict loading guard - prevent fetching if we are streaming/thinking
    if (isLoading) return;

    const hasEndSignal = sseLogs.some(
      (logItem) =>
        (logItem.data as any)?.conversation_id === selectionActiveId &&
        (logItem.data.type === 'stream_end' || logItem.data.type === 'complete' || logItem.data.type === 'conversation_created')
    );
    const selectedByUser = userInteractedRef.current === true;

    if (!hasEndSignal && !selectedByUser) return;

    let cancelled = false;
    (async () => {
      try {
        setIsHydratingHistory(true);
        log('hydrate(widget): start', selectionActiveId);
        const msgs = await historyStrategy.loadFullTranscript(selectionActiveId);
        if (!cancelled && Array.isArray(msgs)) {
           // Only update if we actually got messages to prevent wiping
           if (msgs.length > 0) {
             setMessages(msgs);
             setSseLogs([]);
             loadedForRef.current = selectionActiveId;
             log('hydrate(widget): applied', msgs.length);
           }
        }
      } catch (e) {
        console.warn('[hydrate(widget): error]', e);
      } finally {
        if (!cancelled) setIsHydratingHistory(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isWidget, selectionActiveId, isLoading, historyStrategy.loadFullTranscript, setMessages, sseLogs, messages, isHydratingHistory, log]);

  // --- TEST MODE Hydration (Fixed Tool Result Visibility) ---
  useEffect(() => {
    if (isWidget) return;
    
    const lastLog = sseLogs[sseLogs.length - 1];
    const lastType = lastLog?.data?.type;
    const ended = lastType === 'complete' || lastType === 'stream_end';
    
    if (!ended) return;
    if (!selectionActiveId || selectionActiveId === NEW_CHAT_ID) return;

    // Force refresh in test mode to catch tool outputs that weren't streamed
    const timer = setTimeout(() => {
        if (isLoading) return; 
        log('hydrate(test): executing', selectionActiveId);
        setIsHydratingHistory(true);
        loadHistory(selectionActiveId)
          .finally(() => setIsHydratingHistory(false));
    }, 1000); // 1s delay to allow DB writes to settle

    return () => clearTimeout(timer);
  }, [isWidget, sseLogs, selectionActiveId, loadHistory, isLoading, log]);

  // Adoption
  useEffect(() => {
    const cid = conversationId;
    if (!cid) return;

    const last = messages[messages.length - 1] as any;
    const hasLiveAssistant = last && last.type === 'assistant' && last.isStreaming === true;
    const hasPendingUser = messages.some((m: any) => m.type === 'user' && m.status === 'pending');
    const liveTurn = isLoading || hasLiveAssistant || hasPendingUser;

    if (selectionActiveId === NEW_CHAT_ID) {
      if (!liveTurn && !adoptionLatchRef.current) return;
      adoptActiveConversationId(cid);
      if (isWidget) historyStrategy.list.refresh?.();
      adoptionLatchRef.current = { id: cid, ts: Date.now() };
      log('adopt(new)', cid);
      return;
    }

    if (isWidget && selectionActiveId && selectionActiveId !== cid) {
      adoptActiveConversationId(cid);
      historyStrategy.list.refresh?.();
      adoptionLatchRef.current = { id: cid, ts: Date.now() };
      log('adopt(widget reconcile)', cid);
    }
  }, [conversationId, selectionActiveId, adoptActiveConversationId, isWidget, historyStrategy.list, isLoading, messages, log]);

  useEffect(() => {
    if (!adoptionLatchRef.current) return;
    const last = sseLogs[sseLogs.length - 1]?.data?.type;
    const ended = last === 'complete' || last === 'stream_end';
    if (ended || (!isLoading && messages.some(m => m.type === 'assistant' && (m as any).isStreaming === false))) {
      setTimeout(() => {
          adoptionLatchRef.current = null;
          log('adopt: cleared');
      }, 500);
    }
  }, [sseLogs, isLoading, messages, log]);

  const sessionReady = Boolean(sessionConfig?.wat && sessionConfig?.api_base_url);
  useEffect(() => {
    if (!isWidget || !sessionReady) return;
    const items = sortByUpdatedDesc((historyStrategy.list.items as ConversationSummary[]) || []);
    const saved = conversationPersistence.loadSavedConversationId();
    const latest = items.length > 0 ? items[0].id : null;
    const target = saved || latest || NEW_CHAT_ID;
    setInitialTargetId(target);
    log('initialTarget(widget)', { saved, latest, target });
  }, [isWidget, sessionReady, historyStrategy.list.items, conversationPersistence, log]);

  useEffect(() => {
    if (didAutoSelectRef.current) return;
    if (userInteractedRef.current) return;
    if (!isWidget || !sessionReady) return;
    if (!initialTargetId || initialTargetId === NEW_CHAT_ID) return;
    if (selectionActiveId && selectionActiveId !== NEW_CHAT_ID) return;

    onSelectConversation(initialTargetId);
    didAutoSelectRef.current = true;
    log('autoSelect(widget)', initialTargetId);
  }, [isWidget, sessionReady, initialTargetId, selectionActiveId, onSelectConversation, log]);
  
  useEffect(() => {
    if (selectionActiveId === NEW_CHAT_ID) {
      setMessages([]);
      setSseLogs([]);
      log('switch:new_chat');
    }
  }, [selectionActiveId, setMessages, log]);

  useTransientNewChatMemory({
    scope: memoryScope, agentId: activeAgentId, authMode,
    activeConversationId: selectionActiveId, messages, setMessages, initialMessage,
  });

  const projectBriefManager = useCallback(
    async (args: { action: 'show' | 'hide' | 'update_field' | 'submit'; field?: string; value?: any }, ctx?: any) => {
      const api = ctx?.projectBriefApi;
      if (!api) return { status: 'error', message: 'Project Brief UI is not configured for this chatbot.' };
      switch (args.action) {
        case 'show': api.show?.(); return { status: 'success', message: 'Brief panel shown.' };
        case 'hide': api.hide?.(); return { status: 'success', message: 'Brief panel hidden.' };
        case 'update_field':
          if (typeof args.field === 'string' && typeof args.value !== 'undefined') {
            api.updateField?.(args.field as any, args.value);
            return { status: 'success', message: `Updated field: ${args.field}` };
          }
          return { status: 'error', message: 'Missing field or value for update.' };
        case 'submit':
          return { status: 'error', message: 'Submission must be done by the user in the panel. Please instruct the user to click Submit.', code: 'user_action_required' };
        default:
          return { status: 'error', message: 'Unknown action.' };
      }
    },
    []
  );

  const composedHeadlessTools = useMemo(() => ({ ...(headlessTools || {}), project_brief_manager: projectBriefManager }), [headlessTools, projectBriefManager]);

  const { activeToolCall, onSubmitToolResponse, onCancelTool, onReplayFrontendActions } =
    useHeadlessToolRuntime({
      messages,
      headlessTools: composedHeadlessTools,
      headlessContext,
      submitToolResult,
      isHistoryLoading: isHydratingHistory,
      conversationId: selectionActiveId,
    });

  const assistantState = useAssistantState(isLoading, messages);

  const handleSendMessage = useCallback(
    async (text: string, options?: SendMessageOptions) => {
      if (text.startsWith('/')) {
        onCommand?.({ type: 'slash_command', payload: { command: text } });
        return;
      }
      setSseLogs([]);

      const isFirstTurn = !conversationId;
      const turnContextObject = perTurnContext ? perTurnContext() : undefined;

      let injectionContextJson: string | undefined = undefined;
      try {
        const base = isFirstTurn && initialContext ? { ...initialContext } : {};
        const merged = turnContextObject ? { ...base, ...turnContextObject } : base;
        if (merged && Object.keys(merged).length > 0) injectionContextJson = JSON.stringify(merged);
      } catch (e) { console.warn('[chat] per-turn injectionContext not serializable', e); }

      const turnPromptString = typeof perTurnPrompt === 'function' ? perTurnPrompt() : perTurnPrompt;
      const finalTurnPrompt = turnPromptString || undefined;

      let overlayHash: string | undefined;
      if (isFirstTurn && initialContext) {
        try { const { hash } = await computeConfigHash(initialContext); overlayHash = hash; } catch {}
      }

      setLastSentOverlays({
        initial_context_on_first_turn: isFirstTurn ? initialContext : undefined,
        per_turn_context_object: turnContextObject,
        final_turn_prompt_override: finalTurnPrompt,
        injection_context_len: injectionContextJson?.length || 0,
        overlay_hash: overlayHash,
      });
      setLastReceivedHeaders(null);

      const shouldForceNew = selectionActiveId === NEW_CHAT_ID;
      const messageOptions: any = {
        injectionContext: injectionContextJson,
        turnPromptOverride: finalTurnPrompt,
        overlay_hash: overlayHash,
        ...options,
        conversationId: !shouldForceNew && selectionActiveId ? selectionActiveId : null,
        ...(shouldForceNew ? { forceNew: true, conversationId: null } : {}),
      };

      log('sendMessage', { shouldForceNew, selectionActiveId, hasContext: !!injectionContextJson, hasPrompt: !!finalTurnPrompt });
      await sendMessage(text, messageOptions);
    },
    [onCommand, perTurnContext, perTurnPrompt, initialContext, conversationId, selectionActiveId, sendMessage, log]
  );

  const isInitializing = !isWidget && historyStrategy.list.isLoading && (historyStrategy.list.items?.length ?? 0) === 0;
  const error = !isWidget ? agentsError || historyStrategy.list.error : null;

  const historyItems = sortByUpdatedDesc((historyStrategy.list.items as ConversationSummary[]) || []);
  let baseConversations: ConversationSummary[] = historyItems;

  if (mode === 'full') {
    const shouldInjectPlaceholder = isNewChatPending || baseConversations.length === 0;
    if (shouldInjectPlaceholder && !baseConversations.some(c => c.id === NEW_CHAT_ID)) {
      const nowIso = new Date().toISOString();
      const newChatPlaceholder: ConversationSummary = {
        id: NEW_CHAT_ID, created_at: nowIso, updated_at: nowIso,
        first_user_message_preview: 'New Chat', agent_id: activeAgentId || '', user_id: '',
      };
      baseConversations = [newChatPlaceholder, ...baseConversations];
    }
  }

  const activeId = selectionActiveId;
  const needsSyntheticInjection =
    isWidget && activeId && activeId !== NEW_CHAT_ID && !baseConversations.some((c) => c.id === activeId);

  const finalConversations = needsSyntheticInjection
    ? [
        {
          id: activeId!,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          first_user_message_preview: messages.find((m) => m.type === 'user')?.content || 'New Chat',
          agent_id: activeAgentId || '',
          user_id: '',
        } as ConversationSummary,
        ...baseConversations,
      ]
    : baseConversations;

  const computedIsLoadingMessages = Boolean(isLoading || isHydratingHistory);

  return {
    isInitializing, error, sessionConfig, sseLogs, lastSentOverlays, lastReceivedHeaders,
    isSidebarVisible, setIsSidebarVisible, conversations: finalConversations,
    activeConversationId: selectionActiveId, onSelectConversation,
    isConversationsLoading: historyStrategy.list.isLoading,
    isMoreConversationsLoading: historyStrategy.list.isMoreLoading,
    hasMoreConversations: historyStrategy.list.hasMore,
    onLoadMoreConversations: historyStrategy.list.loadMore,
    agents, activeAgentId, setActiveAgentId, messages,
    isLoadingMessages: computedIsLoadingMessages,
    messageError, assistantState,
    onSendMessage: handleSendMessage, activeToolCall, onSubmitToolResponse,
    onCancelTool, onReplayFrontendActions,
    visitorHistoryError: isWidget ? historyStrategy.list.error : null,
  } as const;
}